import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, TrendingDown, Minus, BarChart3, Clock, MapPin, Activity, Zap, Brain, AlertTriangle, ChevronDown } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine
} from "recharts";
import api from "@/lib/api";
import InsightsPanel from "./InsightsPanel";
import WrappedInsightsSection from "./WrappedInsightsSection";

const MOOD_EMOJI = { 1: "\u{1F622}", 2: "\u{1F622}", 3: "\u{1F615}", 4: "\u{1F615}", 5: "\u{1F610}", 6: "\u{1F642}", 7: "\u{1F642}", 8: "\u{1F604}", 9: "\u{1F929}", 10: "\u{1F929}" };

const ACTIVITY_LABELS = {
  lavoro: "Lavoro", studio: "Studio", pausa: "Pausa", socialita: "Socialit\u00e0",
  sport: "Sport", cura_personale: "Cura pers.", commissioni: "Commissioni", altro: "Altro"
};
const LOCATION_LABELS = {
  casa: "Casa", lavoro_scuola: "Lavoro/Scuola", fuori: "Fuori", in_movimento: "In movim."
};
const SITUATION_LABELS = {
  conflitto: "Conflitto", successo: "Successo", fallimento: "Fallimento", sorpresa: "Sorpresa",
  perdita: "Perdita", supporto_ricevuto: "Supporto ric.", supporto_dato: "Supporto dato",
  decisione: "Decisione", attesa: "Attesa", routine: "Routine", introspezione: "Introspezione"
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded-lg shadow-lg p-2 text-xs">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

const MoodBar = ({ value, max = 10 }) => {
  const pct = (value / max) * 100;
  const color = value >= 4 ? "bg-green-500" : value >= 3 ? "bg-yellow-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2 flex-1">
      <div className="flex-1 bg-muted rounded-full h-2">
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono w-6 text-right">{value}</span>
    </div>
  );
};

const VoiceMoodDashboard = () => {
  const [charts, setCharts] = useState(null);
  const [correlations, setCorrelations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [chartsRes, corrRes] = await Promise.all([
        api.get(`/voice-mood/charts?days=${days}`),
        api.get(`/voice-mood/correlations?days=${days}`),
      ]);
      setCharts(chartsRes.data);
      setCorrelations(corrRes.data);
    } catch {
      // Not enough data
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasData = charts?.timeline?.length > 0;

  // 2026-05-15: InsightsPanel is rendered ALWAYS (even when timeline is empty)
  // because the new descriptive analytics (v2 distributions, cross-tabs) are useful
  // even with 1-2 entries; the panel handles its own empty states.
  if (!hasData) {
    return (
      <div className="space-y-4" data-testid="voice-mood-dashboard">
        {/* 2026-06: Spotify-Wrapped narrative insights at the very top */}
        <WrappedInsightsSection />
        <InsightsPanel />
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <BarChart3 className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm font-medium">Timeline ancora vuota</p>
            <p className="text-xs text-muted-foreground mt-1">Registra almeno 3 check-in per vedere il grafico temporale.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const avgMood = correlations?.overall_avg_mood || 0;
  const totalEntries = correlations?.total_entries || 0;

  return (
    <div className="space-y-4" data-testid="voice-mood-dashboard">
      {/* ===== 2026-06: Spotify-Wrapped narrative insights (TOP) ===== */}
      <WrappedInsightsSection />

      {/* ===== NEW (2026-05-15): Descriptive EMA insights — v2 distributions, cross-tabs ===== */}
      <InsightsPanel />

      {/* ===== LEGACY: timeline-based dashboard (collapsed by default) ===== */}
      <details className="group" data-testid="full-data-list-details">
        <summary className="cursor-pointer list-none flex items-center justify-between gap-2 py-2.5 px-3 rounded-md border border-dashed border-stone-300 bg-stone-50/40 hover:bg-stone-100/50 transition-colors">
          <span className="text-xs sm:text-sm font-medium text-stone-700">
            Vedi elenco completo (grafici e correlazioni)
          </span>
          <ChevronDown className="w-4 h-4 text-stone-500 group-open:rotate-180 transition-transform" />
        </summary>
        <div className="space-y-4 pt-3 pb-1">

      {/* Period selector */}
      <div className="flex gap-2 pt-2 border-t">
        {[7, 14, 30, 90].map((d) => (
          <Button key={d} variant={days === d ? "default" : "outline"} size="sm" className="text-xs h-7" onClick={() => setDays(d)} data-testid={`period-${d}`}>
            {d}g
          </Button>
        ))}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-2">
        <Card className="border">
          <CardContent className="p-3 text-center">
            <span className="text-2xl">{MOOD_EMOJI[Math.round(avgMood)]}</span>
            <p className="text-lg font-bold">{avgMood}/10</p>
            <p className="text-[10px] text-muted-foreground">Umore medio</p>
          </CardContent>
        </Card>
        <Card className="border">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold">{totalEntries}</p>
            <p className="text-[10px] text-muted-foreground">Check-in</p>
          </CardContent>
        </Card>
        <Card className="border">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold">{charts.timeline.length}</p>
            <p className="text-[10px] text-muted-foreground">Giorni attivi</p>
          </CardContent>
        </Card>
      </div>

      {/* Insights */}
      {correlations?.insights?.length > 0 && (
        <Card className="border border-indigo-200 bg-indigo-50/50">
          <CardContent className="p-3">
            <h3 className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Brain className="w-3.5 h-3.5" /> Pattern identificati
            </h3>
            <div className="space-y-1.5">
              {correlations.insights.map((insight) => (
                <p key={insight.substring(0, 25)} className="text-xs text-indigo-900 leading-relaxed">{insight}</p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline chart */}
      {charts.timeline.length >= 2 && (
        <Card className="border shadow-sm">
          <CardHeader className="pb-1 px-3 pt-3">
            <CardTitle className="text-xs sm:text-sm flex items-center gap-1">
              <TrendingUp className="w-4 h-4" /> Andamento umore nel tempo
            </CardTitle>
          </CardHeader>
          <CardContent className="px-1 pb-3">
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={charts.timeline}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 9 }} />
                <YAxis domain={[1, 10]} ticks={[1, 3, 5, 7, 10]} tick={{ fontSize: 9 }} />
                <Tooltip content={<ChartTooltip />} />
                <ReferenceLine y={5} stroke="#94a3b8" strokeDasharray="3 3" />
                <Line type="monotone" dataKey="mood" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} name="Umore" />
                {charts.timeline.some((t) => t.energy) && (
                  <Line type="monotone" dataKey="energy" stroke="#eab308" strokeWidth={1.5} dot={{ r: 2 }} strokeDasharray="4 4" name="Energia" />
                )}
                {charts.timeline.some((t) => t.stress) && (
                  <Line type="monotone" dataKey="stress" stroke="#ef4444" strokeWidth={1.5} dot={{ r: 2 }} strokeDasharray="4 4" name="Stress" />
                )}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Time of day + Weekday */}
      <div className="grid sm:grid-cols-2 gap-3">
        {charts.by_time_of_day.some((t) => t.count > 0) && (
          <Card className="border shadow-sm">
            <CardHeader className="pb-1 px-3 pt-3">
              <CardTitle className="text-xs sm:text-sm flex items-center gap-1">
                <Clock className="w-4 h-4" /> Per momento della giornata
              </CardTitle>
            </CardHeader>
            <CardContent className="px-1 pb-3">
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={charts.by_time_of_day.filter((t) => t.count > 0)}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 9 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="avg_mood" name="Umore" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {charts.by_weekday.some((w) => w.count > 0) && (
          <Card className="border shadow-sm">
            <CardHeader className="pb-1 px-3 pt-3">
              <CardTitle className="text-xs sm:text-sm flex items-center gap-1">
                <Clock className="w-4 h-4" /> Per giorno della settimana
              </CardTitle>
            </CardHeader>
            <CardContent className="px-1 pb-3">
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={charts.by_weekday.filter((w) => w.count > 0)}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 9 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="avg_mood" name="Umore" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Location + Activity */}
      <div className="grid sm:grid-cols-2 gap-3">
        {charts.by_location.length > 0 && (
          <Card className="border shadow-sm">
            <CardHeader className="pb-2 px-3 pt-3">
              <CardTitle className="text-xs sm:text-sm flex items-center gap-1">
                <MapPin className="w-4 h-4" /> Umore per luogo
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 space-y-2">
              {charts.by_location.map((l) => (
                <div key={l.location} className="flex items-center gap-2 text-xs">
                  <span className="w-16 truncate text-muted-foreground">{LOCATION_LABELS[l.location] || l.location}</span>
                  <MoodBar value={l.avg_mood} />
                  <Badge variant="secondary" className="text-[9px] shrink-0">{l.count}x</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {charts.by_activity.length > 0 && (
          <Card className="border shadow-sm">
            <CardHeader className="pb-2 px-3 pt-3">
              <CardTitle className="text-xs sm:text-sm flex items-center gap-1">
                <Activity className="w-4 h-4" /> Umore per attività
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 space-y-2">
              {charts.by_activity.map((a) => (
                <div key={a.activity} className="flex items-center gap-2 text-xs">
                  <span className="w-20 truncate text-muted-foreground">{ACTIVITY_LABELS[a.activity] || a.activity}</span>
                  <MoodBar value={a.avg_mood} />
                  <Badge variant="secondary" className="text-[9px] shrink-0">{a.count}x</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Situation */}
      {charts.by_situation?.length > 0 && (
        <Card className="border shadow-sm">
          <CardHeader className="pb-2 px-3 pt-3">
            <CardTitle className="text-xs sm:text-sm flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> Umore per tipo di situazione
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            {charts.by_situation.map((s) => (
              <div key={s.situation} className="flex items-center gap-2 text-xs">
                <span className="w-20 truncate text-muted-foreground">{SITUATION_LABELS[s.situation] || s.situation}</span>
                <MoodBar value={s.avg_mood} />
                <Badge variant="secondary" className="text-[9px] shrink-0">{s.count}x</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Causal Factors */}
      {charts.causal_factors_freq.length > 0 && (
        <Card className="border shadow-sm">
          <CardHeader className="pb-2 px-3 pt-3">
            <CardTitle className="text-xs sm:text-sm flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" /> Fattori che influenzano l'umore
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            {charts.causal_factors_freq.map((cf) => (
              <div key={cf.factor} className="flex items-center gap-2">
                <Badge variant={cf.valence === "positivo" ? "outline" : "destructive"} className="text-[10px] shrink-0">
                  {cf.valence === "positivo" ? "+" : "-"}
                </Badge>
                <span className="text-xs flex-1 truncate">{cf.factor}</span>
                <MoodBar value={cf.avg_mood} />
                <span className="text-[10px] text-muted-foreground shrink-0">{cf.count}x</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Top correlations */}
      {correlations?.correlations?.length > 0 && (
        <Card className="border shadow-sm">
          <CardHeader className="pb-2 px-3 pt-3">
            <CardTitle className="text-xs sm:text-sm flex items-center gap-1">
              <BarChart3 className="w-4 h-4" /> Correlazioni principali
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-1.5">
            {correlations.correlations.slice(0, 8).map((c) => {
              const icon = c.impact === "positivo" ? <TrendingUp className="w-3.5 h-3.5 text-green-600" /> :
                c.impact === "negativo" ? <TrendingDown className="w-3.5 h-3.5 text-red-500" /> :
                <Minus className="w-3.5 h-3.5 text-gray-400" />;
              return (
                <div key={`${c.variable}-${c.value}`} className={`flex items-center gap-2 p-1.5 rounded text-xs ${
                  c.impact === "positivo" ? "bg-green-50" : c.impact === "negativo" ? "bg-red-50" : "bg-muted/50"
                }`}>
                  {icon}
                  <span className="flex-1 truncate">{c.variable}: {c.value}</span>
                  <span className="font-mono shrink-0">{c.avg_mood}</span>
                  <Badge variant={c.impact === "positivo" ? "outline" : c.impact === "negativo" ? "destructive" : "secondary"} className="text-[9px]">
                    {c.diff_from_overall > 0 ? "+" : ""}{c.diff_from_overall.toFixed(1)}
                  </Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
        </div>
      </details>
    </div>
  );
};

export default VoiceMoodDashboard;
